import React, { JSX } from 'react';

import { IRouteObject } from '@/router/interfaces.ts';
import { LayoutConfigurationPage } from '../layouts/LayoutConfiguration/layout-configuration-factory.ts';
import { Logger } from '@moment/utils/srv-logger.ts';

export interface MomentBaseProps<P, T> {
  queryFn: (params: P) => Promise<T>;
}

export const buildMomentRoute = <TParams extends Record<string, string>>(
  route: MomentRoute<TParams, any>
): Partial<IRouteObject> => {
  return {
    id: route.id,
    path: route.path.raw,
    title: route.title,
    description: route.description,
    sourceLink: route.sourceLink,
    element: React.cloneElement(route.element, {
      queryFn: route.prefetchQuery,
    }),
    prefetchQuery: route.prefetchQuery,
    prefetchQueryKey: route.prefetchQueryKey,
    feedRoute: route.feedRoute,
    prefetchQuerys: route.prefetchQuerys,
    serverHydrate: route.serverHydrate,
    prefetchQueryKeyMutator: route.prefetchQueryKeyMutator,
  };
};

interface PathType<TParams extends Record<string, string>> {
  raw: string;
  build: (params: TParams, query?: Record<string, string>) => string;
}

export function createPathType<TParams extends Record<string, string>>(
  raw: string
): PathType<TParams> {
  return {
    raw,
    build: (params: TParams, query?: Record<string, string>) => {
      let path = raw;
      const paramKeys =
        raw.match(/:[a-zA-Z]+/g)?.map((p) => {
          return p.slice(1);
        }) || [];
      for (const key of paramKeys) {
        if (!(key in params)) {
          throw new Error(`Missing required param '${key}' for path '${raw}'`);
        }
        path = path.replace(`:${key}`, params[key]);
      }
      if (query) {
        const queryString = new URLSearchParams(query).toString();
        return `${path}?${queryString}`;
      }
      return path;
    },
  };
}

export interface RoutePrefetchQueryOptions {
  logger?: Logger;
}

export type RoutePreFetchFunction<TParams, PrefetchType> = (
  params: TParams
) => (options?: RoutePrefetchQueryOptions) => Promise<PrefetchType>;

export type RoutePrefetchQuery<TParams, PrefetchType> = RoutePreFetchFunction<
  TParams,
  PrefetchType
>;

export type MomentRoute<TParams extends Record<string, string>, PrefetchType> = {
  id?: string;
  path: PathType<TParams>;
  title?: string;
  description?: string;
  sourceLink?: string;
  element: JSX.Element;
  prefetchQuery?: RoutePrefetchQuery<TParams, PrefetchType>;
  prefetchQuerys?: {
    queryKey: string;
    queryFn: RoutePrefetchQuery<TParams, any>;
  }[];
  prefetchQueryKey?: string;
  feedRoute?: LayoutConfigurationPage;
  /**
   * A generic function to hydrate server side a dynamic tripod page.
   * @param params
   */
  serverHydrate: (params: TParams) => Promise<any> | null;
  prefetchQueryKeyMutator: (params: any) => string[];
};

export type GoogleTagManagerConfig = {
  product: string;
  property: string;
  env: 'live' | 'sandbox';
  partner: string;
};
